<template>
  <!-- 版式 1:1 照后台原型「小红书活动：审核弹窗（通过/不通过）」（prototype/挚达后台原型-20260909.html#xhsAuditModal）：
       蓝色渐变头 + 图标 + 标题/副标题 + 关闭、白色内容卡（提示条 + 必填标签 + 输入）、右对齐按钮组。
       刻意不用 el-dialog：原型是自绘弹窗（480px、圆角 14、遮罩 rgba(0,0,0,.5)），套 el-dialog 版式会变。 -->
  <div v-if="visible" class="modal-overlay show" @click.self="close">
    <div class="xhs-audit-modal">
      <div class="xhs-audit-header">
        <div class="xhs-audit-header-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.95)"
            stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 11l3 3L22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
        </div>
        <div class="xhs-audit-header-text">
          <div class="xhs-audit-title">{{ isPass ? '审核通过' : '审核不通过' }}</div>
          <div class="xhs-audit-subtitle">
            {{ isPass ? '审核通过该笔记，填写预期奖励月数（仅作参考）' : '请填写不通过原因，该原因将对用户可见' }}
          </div>
        </div>
        <button class="xhs-audit-close" @click="close">&times;</button>
      </div>

      <div class="xhs-audit-body">
        <!-- 通过：预期奖励月数 -->
        <div v-if="isPass" class="xhs-audit-card">
          <div class="xhs-audit-notice">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4a7cf7" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <span>预期奖励月数仅作为参考，最终根据实际配置发放。</span>
          </div>
          <div class="xhs-audit-field-label">预期奖励月数<span class="req">*</span></div>
          <input type="number" min="1" class="xhs-audit-input" v-model="expectedMonths"
            placeholder="请输入预期奖励月数" />
        </div>

        <!-- 不通过：原因（限 100 字，带字数计数器） -->
        <div v-else class="xhs-audit-card">
          <div class="xhs-audit-notice">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4a7cf7" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <span>不通过原因将在小程序端对用户可见，请规范填写。</span>
          </div>
          <div class="xhs-audit-field-label">不通过原因<span class="req">*</span></div>
          <textarea class="xhs-audit-textarea" maxlength="100" v-model="rejectReason"
            placeholder="请填写不通过原因（小程序端对用户可见，限100字）"></textarea>
          <div class="xhs-audit-counter"><span>{{ rejectReason.length }}</span>/100</div>
        </div>
      </div>

      <div class="xhs-audit-footer">
        <button class="xhs-audit-btn cancel" @click="close">取 消</button>
        <button class="xhs-audit-btn confirm" :disabled="submitting" @click="submit">
          {{ submitting ? '提交中…' : '确认审核' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 698 小红书笔记审核弹窗（ADM-3）—— 列表页与「订单详情-小红书 Tab」共用。
 *
 * 用法：<XhsAuditDialog :visible.sync="auditVisible" :row="row" :mode="'pass' | 'reject'" @success="onSuccess" />
 * 版式与文案照后台原型；`mode` 由行内「审核通过 / 审核不通过」两个按钮决定（原型同款交互，
 * 弹窗内不再放「审核结果」单选，也不放审核人字段——审核人由后端取登录态并在列表展示）。
 */
export default {
  name: "XhsAuditDialog",
  props: {
    visible: { type: Boolean, default: false },
    row: { type: Object, default: null },
    /** pass：审核通过；reject：审核不通过 */
    mode: { type: String, default: "pass" },
  },
  data() {
    return {
      submitting: false,
      expectedMonths: "",
      rejectReason: "",
    };
  },
  computed: {
    isPass() {
      return this.mode !== "reject";
    },
  },
  watch: {
    visible(val) {
      if (val) this.reset();
    },
  },
  methods: {
    reset() {
      const row = this.row || {};
      this.expectedMonths = row.expectedMonths == null ? "" : String(row.expectedMonths);
      this.rejectReason = "";
      this.submitting = false;
    },
    close() {
      this.$emit("update:visible", false);
    },
    submit() {
      const _ = this;
      // 校验文案与原型一致
      if (_.isPass) {
        const n = parseInt(_.expectedMonths, 10);
        if (!_.expectedMonths || isNaN(n) || n < 1) {
          _.$U.error("请输入预期奖励月数（正整数）");
          return;
        }
      } else if (!_.rejectReason.trim()) {
        _.$U.error("请填写不通过原因");
        return;
      }
      // 通过时不带原因、不通过时不带月数，避免把无关字段交给后端
      const params = {
        noteNo: (_.row || {}).noteNo,
        pass: _.isPass,
        expectedMonths: _.isPass ? parseInt(_.expectedMonths, 10) : undefined,
        rejectReason: _.isPass ? undefined : _.rejectReason.trim(),
      };
      _.submitting = true;
      _.$H.post(_, _.$A.xhsActivity.audit, params)
        .then(() => {
          _.$U.success("审核完成");
          _.close();
          _.$emit("success");
        })
        .finally(() => {
          _.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
/* ===== 样式照后台原型（自包含，跨页通用）===== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: none;
  justify-content: center;
  align-items: center;
  padding: 20px;
  overflow-y: auto;
}
.modal-overlay.show {
  display: flex;
}
.xhs-audit-modal {
  width: 480px;
  max-width: 94vw;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 12px 40px rgba(30, 60, 130, 0.18);
}
.xhs-audit-header {
  background: linear-gradient(135deg, #4a7cf7 0%, #6aa5ff 55%, #7fb2ff 100%);
  padding: 18px 22px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.xhs-audit-header-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
}
.xhs-audit-header-text {
  flex: 1;
  min-width: 0;
}
.xhs-audit-title {
  color: #fff;
  font-size: 17px;
  font-weight: 600;
}
.xhs-audit-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  margin-top: 3px;
}
.xhs-audit-close {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.xhs-audit-close:hover {
  background: rgba(255, 255, 255, 0.32);
}
.xhs-audit-body {
  padding: 18px 22px;
  background: #f5f7fb;
}
.xhs-audit-card {
  background: #fff;
  border-radius: 10px;
  padding: 16px 18px;
  box-shadow: 0 1px 4px rgba(20, 50, 100, 0.06);
}
.xhs-audit-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: linear-gradient(135deg, #f0f7ff, #f4f9ff);
  border: 1px solid #d3e7ff;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  color: #4a7cf7;
  margin-bottom: 14px;
  line-height: 1.6;
}
.xhs-audit-notice svg {
  flex-shrink: 0;
  margin-top: 1px;
}
.xhs-audit-field-label {
  font-size: 12px;
  color: #9aa5bd;
  margin-bottom: 6px;
}
.xhs-audit-field-label .req {
  color: #ff6b6b;
  margin-left: 2px;
}
.xhs-audit-input,
.xhs-audit-textarea {
  width: 100%;
  border: 1px solid #d7deeb;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #33415c;
  outline: none;
  box-sizing: border-box;
  background: #fff;
  transition: all 0.2s;
  font-family: inherit;
}
.xhs-audit-input {
  height: 36px;
}
.xhs-audit-textarea {
  padding: 10px 12px;
  min-height: 88px;
  resize: vertical;
  line-height: 1.6;
}
.xhs-audit-input:focus,
.xhs-audit-textarea:focus {
  border-color: #4a7cf7;
  box-shadow: 0 0 0 3px rgba(74, 124, 247, 0.12);
}
.xhs-audit-counter {
  text-align: right;
  font-size: 12px;
  color: #9aa5bd;
  margin-top: 6px;
}
.xhs-audit-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 14px 22px 18px;
  background: #f5f7fb;
}
.xhs-audit-btn {
  height: 38px;
  padding: 0 20px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid transparent;
}
.xhs-audit-btn.cancel {
  background: #fff;
  border-color: #d7deeb;
  color: #5a6472;
}
.xhs-audit-btn.cancel:hover {
  border-color: #b6c0d4;
}
.xhs-audit-btn.confirm {
  background: linear-gradient(135deg, #4a7cf7, #7fb2ff);
  color: #fff;
}
.xhs-audit-btn.confirm:hover {
  opacity: 0.92;
}
.xhs-audit-btn.confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
